// ============================================================================
// A Save press during an outage says why it did not save (review of the
// outage lane, 2026-09-30)
// ----------------------------------------------------------------------------
// While QuoteMaker's server is not answering, a project save keeps the edits
// on this device and rejects without a message of its own, because the server
// banner owns the status. use-project-persistence marks that rejection
// `quietWhileServerAway`. The banner can be dismissed, and then a press of
// Save (the nav SAVE disk, Ctrl+S, the Stage 5 save) flashed SAVING, went back
// to SAVE and left nothing on screen, plus an uncaught rejection per press.
// The operator's words were "cant save". A press now answers in the banner's
// own words (BackendOfflineBanner.tsx).
//
// Any other failure is left to the surface that raised it: a save the server
// refused shows the save-error banner and a toast, a conflict opens the
// conflict banner, a newer version to review opens its notice.
// ============================================================================

export const MANUAL_SAVE_KEPT_NOTICE = "Not saved yet. QuoteMaker is updating or can't reach its server. Your changes are kept on this device and will save when it answers.";
export const MANUAL_SAVE_ON_SCREEN_NOTICE = "Not saved yet. QuoteMaker is updating or can't reach its server. Your changes are on this screen and will save when it answers. Keep this tab open until then.";

/** The sentence a Save press shows, or null when the save already said why. */
export function manualSaveOutageNotice(err: unknown): string | null {
    if (!err || typeof err !== 'object') return null;
    const record = err as { quietWhileServerAway?: unknown; keptOnDevice?: unknown };
    if (record.quietWhileServerAway !== true) return null;
    return record.keptOnDevice === true ? MANUAL_SAVE_KEPT_NOTICE : MANUAL_SAVE_ON_SCREEN_NOTICE;
}

/**
 * Runs one Save press. Resolves false when the save rejected or resolved false, true otherwise,
 * and never rejects, so a press cannot leave an uncaught promise.
 */
export async function runManualSave(
    save: () => Promise<unknown>,
    notify: (message: string) => void,
): Promise<boolean> {
    try {
        // A save that RESOLVES false refused without throwing (the Stage 5 Save does): that is not a success.
        return (await save()) !== false;
    } catch (err) {
        const notice = manualSaveOutageNotice(err);
        if (notice) notify(notice);
        else console.warn('[QM] Save did not complete:', (err as { message?: string })?.message || err);
        return false;
    }
}
